import { useEffect, useState, useMemo } from 'react';
import clsx from 'clsx';
import {
  Users,
  Search,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Filter,
  MessageSquare,
  UserCheck,
} from 'lucide-react';
import { joinRequestsApi } from '../../../api/client';
import type { OrgJoinRequest } from '../../../api/client';
import { usePermissions } from '../../../hooks/usePermissions';

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getInitials(displayName: string | null, email: string): string {
  if (displayName) {
    const parts = displayName.trim().split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0].substring(0, 2).toUpperCase();
  }
  return email.substring(0, 2).toUpperCase();
}

// ── Status Badge ───────────────────────────────────────────────────────────────

type RequestStatus = 'pending' | 'accepted' | 'denied';

const STATUS_STYLES: Record<RequestStatus, string> = {
  pending:  'text-brand-light bg-brand/10 border-brand/30',
  accepted: 'text-win bg-win/10 border-win/30',
  denied:   'text-loss bg-loss/10 border-loss/30',
};

const STATUS_ICONS: Record<RequestStatus, React.ElementType> = {
  pending:  Clock,
  accepted: CheckCircle,
  denied:   XCircle,
};

function StatusBadge({ status }: { status: RequestStatus }) {
  const Icon = STATUS_ICONS[status];
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide border rounded px-1.5 py-0.5',
        STATUS_STYLES[status],
      )}
    >
      <Icon className="w-3 h-3" />
      {status}
    </span>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

type StatusFilter = 'all' | RequestStatus;

export function AdminJoinRequestsPage() {
  const { can } = usePermissions();
  const canManage = can('members.invite');

  const [requests,    setRequests]    = useState<OrgJoinRequest[]>([]);
  const [isLoading,   setIsLoading]   = useState(true);
  const [error,       setError]       = useState<string | null>(null);
  const [search,      setSearch]      = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [success,     setSuccess]     = useState<string | null>(null);

  const load = () => {
    setIsLoading(true);
    joinRequestsApi
      .list()
      .then(setRequests)
      .catch((err) => setError((err as Error).message ?? 'Failed to load join requests'))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return requests.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (q) {
        const name = (r.userDisplayName ?? '').toLowerCase();
        const email = r.userEmail.toLowerCase();
        if (!name.includes(q) && !email.includes(q)) return false;
      }
      return true;
    });
  }, [requests, search, statusFilter]);

  const pendingCount = useMemo(() => requests.filter((r) => r.status === 'pending').length, [requests]);

  const handleAccept = async (id: string) => {
    setActionLoading(id);
    setError(null);
    try {
      await joinRequestsApi.accept(id);
      setRequests((prev) =>
        prev.map((r) => r.id === id ? { ...r, status: 'accepted' as RequestStatus } : r),
      );
      setSuccess('Request accepted — user has been added to your organization.');
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept request');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeny = async (id: string) => {
    setActionLoading(id);
    setError(null);
    try {
      await joinRequestsApi.deny(id);
      setRequests((prev) =>
        prev.map((r) => r.id === id ? { ...r, status: 'denied' as RequestStatus } : r),
      );
      setSuccess('Request denied.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deny request');
    } finally {
      setActionLoading(null);
    }
  };

  const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
    { value: 'all',      label: 'All' },
    { value: 'pending',  label: 'Pending' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'denied',   label: 'Denied' },
  ];

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-100 flex items-center gap-2">
            Join Requests
            {pendingCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold rounded-full bg-brand/20 text-brand border border-brand/30">
                {pendingCount}
              </span>
            )}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Review and approve users who have requested to join your organization.
          </p>
        </div>
      </div>

      {/* Feedback */}
      {success && (
        <div className="flex items-center gap-3 p-3 bg-win/10 border border-win/20 rounded-lg">
          <CheckCircle className="w-4 h-4 text-win flex-shrink-0" />
          <p className="text-sm text-win">{success}</p>
        </div>
      )}
      {error && (
        <div className="flex items-center gap-3 p-3 bg-loss/10 border border-loss/20 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-loss flex-shrink-0" />
          <p className="text-sm text-loss">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-loss/60 hover:text-loss" aria-label="Dismiss">×</button>
        </div>
      )}

      {/* Search + filter + refresh */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-600 pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full pl-8 pr-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-brand/60 transition-colors"
          />
        </div>
        <div className="flex items-center gap-1 bg-surface-2 border border-surface-4 rounded-md p-1">
          <Filter className="w-3.5 h-3.5 text-gray-600 ml-1" />
          {STATUS_FILTERS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setStatusFilter(value)}
              className={clsx(
                'px-2.5 py-1 rounded text-xs font-medium transition-colors',
                statusFilter === value
                  ? 'bg-brand/20 text-brand'
                  : 'text-gray-500 hover:text-gray-300',
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          onClick={load}
          className="p-2 text-gray-500 hover:text-gray-300 hover:bg-surface-3 rounded-md transition-colors"
          aria-label="Refresh"
        >
          <RefreshCw className={clsx('w-4 h-4', isLoading && 'animate-spin')} />
        </button>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-8 px-5">
            <RefreshCw className="w-4 h-4 animate-spin" />
            Loading join requests…
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center py-12 text-gray-500 gap-2">
            <Users className="w-8 h-8 text-gray-700" />
            <p className="text-sm">
              {search || statusFilter !== 'all'
                ? 'No requests match your filters.'
                : 'No join requests yet.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" role="table">
              <thead>
                <tr className="border-b border-surface-4">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Message</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Submitted</th>
                  {canManage && <th className="w-40 px-4 py-3" />}
                </tr>
              </thead>
              <tbody>
                {filtered.map((req) => {
                  const initials = getInitials(req.userDisplayName, req.userEmail);
                  const isActing = actionLoading === req.id;
                  return (
                    <tr
                      key={req.id}
                      className="border-b border-surface-4/50 last:border-0 hover:bg-surface-2/30 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-brand/20 border border-brand/30 flex items-center justify-center flex-shrink-0">
                            <span className="text-[11px] font-semibold text-brand">{initials}</span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-100 truncate">
                              {req.userDisplayName ?? req.userEmail}
                            </p>
                            <p className="text-xs text-gray-500 truncate md:hidden">{req.userEmail}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 hidden md:table-cell truncate max-w-[200px]">
                        {req.userEmail}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={req.status as RequestStatus} />
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 hidden lg:table-cell max-w-[220px]">
                        {req.message ? (
                          <span className="flex items-start gap-1.5">
                            <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0 text-gray-600" />
                            <span className="truncate" title={req.message}>{req.message}</span>
                          </span>
                        ) : (
                          <span className="text-gray-700">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 hidden sm:table-cell">
                        {formatDate(req.createdAt)}
                      </td>
                      {canManage && (
                        <td className="px-4 py-3">
                          {req.status === 'pending' && (
                            <div className="flex items-center gap-1 justify-end">
                              <button
                                onClick={() => void handleAccept(req.id)}
                                disabled={isActing}
                                className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-win hover:text-win/80 hover:bg-win/10 rounded transition-colors disabled:opacity-50"
                              >
                                {isActing ? <RefreshCw className="w-3 h-3 animate-spin" /> : <UserCheck className="w-3 h-3" />}
                                Accept
                              </button>
                              <button
                                onClick={() => void handleDeny(req.id)}
                                disabled={isActing}
                                className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-loss hover:text-loss/80 hover:bg-loss/10 rounded transition-colors disabled:opacity-50"
                              >
                                <XCircle className="w-3 h-3" />
                                Deny
                              </button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-600 text-center">
        {filtered.length} {filtered.length === 1 ? 'request' : 'requests'}
        {requests.length !== filtered.length && ` of ${requests.length}`}
      </p>
    </div>
  );
}
